import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import AuthPanel from '../components/auth/AuthPanel';
import StorefrontLayout from '../components/layout/StorefrontLayout';

function AuthPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [authMode, setAuthMode] = React.useState<'login' | 'register'>(
    searchParams.get('mode') === 'register' ? 'register' : 'login',
  );

  React.useEffect(() => {
    setAuthMode(searchParams.get('mode') === 'register' ? 'register' : 'login');
  }, [searchParams]);

  const isRegisterMode = authMode === 'register';
  const accountJustDeleted = Boolean(
    (location.state as { accountDeleted?: boolean } | null)?.accountDeleted,
  );

  const navigateAuthKeepingQuery = (mutate: (p: URLSearchParams) => void) => {
    const p = new URLSearchParams(location.search);
    mutate(p);
    const q = p.toString();
    navigate(q ? `/auth?${q}` : '/auth', { replace: true });
  };

  return (
    <StorefrontLayout>
      <div className="w-full bg-slate-50 px-4 py-8 sm:px-6 sm:py-12 md:py-16">
        {accountJustDeleted ? (
          <div className="mx-auto mb-6 max-w-lg">
            <div
              role="status"
              className="rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
            >
              {t('auth.account_deleted_notice')}
            </div>
          </div>
        ) : null}

        {isRegisterMode ? (
          <div className="mx-auto w-full max-w-2xl">
            <div className="rounded-sm border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-6 py-5 sm:px-8">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  {t('auth.tab_register', { defaultValue: 'Create account' })}
                </p>
                <h1 className="mt-1 text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                  {t('auth.register_title', { defaultValue: 'Create your account' })}
                </h1>
                <p className="mt-2 text-sm text-slate-600">
                  {t('auth.register_subtitle', {
                    defaultValue:
                      'Enter your details and delivery address to start ordering.',
                  })}
                </p>
              </div>
              <div className="px-6 py-6 sm:px-8 sm:py-8">
                <AuthPanel
                  lockMode="register"
                  minimal
                  initialMode={authMode}
                  onModeChange={(next) => {
                    setAuthMode(next);
                    navigateAuthKeepingQuery((p) => {
                      if (next === 'register') p.set('mode', 'register');
                      else p.delete('mode');
                    });
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="mx-auto grid w-full max-w-5xl gap-0 overflow-hidden rounded-sm border border-slate-200 bg-white shadow-sm lg:grid-cols-[1fr_420px]">
            <section className="hidden border-r border-slate-200 bg-slate-50 px-8 py-10 lg:flex lg:flex-col lg:justify-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                {t('hero.badge')}
              </p>
              <h1 className="mt-3 max-w-md text-2xl font-semibold leading-tight tracking-tight text-slate-900 xl:text-3xl">
                {t('hero.title_part1')}{' '}
                <span className="text-[#ff4b2b]">{t('hero.title_part2')}</span>
              </h1>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-600">
                {t('hero.subtitle')}
              </p>
              <ul className="mt-8 space-y-3 border-t border-slate-200 pt-6 text-sm text-slate-700">
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1 w-1 shrink-0 bg-slate-900" aria-hidden />
                  {t('hero.feature_global_body')}
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1 w-1 shrink-0 bg-slate-900" aria-hidden />
                  {t('hero.feature_stock_body')}
                </li>
              </ul>
            </section>

            <section className="px-6 py-8 sm:px-8 sm:py-10">
              <div className="mb-6 lg:hidden">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  {t('hero.badge')}
                </p>
                <h1 className="mt-1 text-lg font-semibold text-slate-900">
                  {t('auth.sign_in_title', { defaultValue: 'Sign in' })}
                </h1>
              </div>
              <div className="hidden lg:block">
                <h1 className="text-lg font-semibold tracking-tight text-slate-900">
                  {t('auth.sign_in_title', { defaultValue: 'Sign in' })}
                </h1>
                <p className="mt-1 text-sm text-slate-600">
                  {t('auth.sign_in_subtitle', {
                    defaultValue: 'Access your orders and account settings.',
                  })}
                </p>
              </div>
              <div className="mt-6">
                <AuthPanel
                  lockMode="login"
                  minimal
                  initialMode={authMode}
                  onModeChange={(next) => {
                    setAuthMode(next);
                    navigateAuthKeepingQuery((p) => {
                      if (next === 'register') p.set('mode', 'register');
                      else p.delete('mode');
                    });
                  }}
                />
              </div>
            </section>
          </div>
        )}
      </div>
    </StorefrontLayout>
  );
}

export default AuthPage;
